import { useState, useMemo, useEffect, useCallback } from 'react'
import { Box, Stack, Typography, Chip, Button, Tabs, Tab } from '@mui/material'
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FavoriteIcon from '@mui/icons-material/Favorite'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { useIsMobile } from '@/hooks/Responsive'
import BasicModal from '@components/Modal/BasicModal.jsx';
import AlertConfirmModal from '@components/Modal/AlertConfirmModal.jsx';
import { getXrCntsDetail, insertXrCntsHistory } from '@/api/vx'
import { useAuth } from '@/components/AuthContext'
import imgType1 from '@assets/images/sub/img_license_1.svg'
import imgType2 from '@assets/images/sub/img_license_2.svg'
import imgType3 from '@assets/images/sub/img_license_3.svg'
import imgType4 from '@assets/images/sub/img_license_4.svg'
import imgOpen from '@assets/images/sub/img_license_open.png'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'
import { deleteMyContentsConcern, insertMyContentsConcern } from '@/api/mypage';
import { cleanHtml } from '@/api/util';

export function TabPanel({ children, value, index, ...other }) {
  return (
      <div
          role="tabpanel"
          hidden={value !== index}
          id={`tabpanel-${index}`}
          aria-labelledby={`tab-${index}`}
          {...other}
      >
        {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
      </div>
  )
}

export default function XRContentsView() {
  const getXRColor = (xr) => {
    if (xr === 'VL-PC') return 'secondary'
    if (xr === 'VL-MOB') return 'secondary2'
    if (xr === 'VL-ASYNC') return 'primary'
    if (xr === 'VL-HMD') return 'error'
    if (xr === 'VL-META') return 'error'
    return 'grey'
  }

  const navigate = useNavigate()

  const { user } = useAuth();

  const { id } = useParams()
  const { isMobile } = useIsMobile()

  const [selected, setSelected] = useState(null)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const [previewOpen, setPreviewOpen] = useState(false);
  const [experienceOpen, setExperienceOpen] = useState(false);

  const [previewSelected, setPreviewSelected] = useState(null);
  const [experienceSelected, setExperienceSelected] = useState(null);

  const [downloadConfirmOpen, setDownloadConfirmOpen] = useState(false)

  const [iframeKey, setIframeKey] = useState(0) // iframe 재생성

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true)

  const location = useLocation();
  const fromList = location.state?.row;

  const lang = useLang()

  // 미리보기 클릭
  const handlePreviewClick = (row) => {
    setPreviewSelected(row);
    setPreviewOpen(true);
  };

  // 미리보기 닫기
  const handleClosePreview = () => {
    setPreviewOpen(false)
    setIframeKey(prev => prev + 1)
  }

  // HMD일때 다운로드 컨펌창
  const handleDownloadConfirm = () => {
    const url = data?.experienceUrl
    if (!url) return

    const a = document.createElement('a')
    a.href = url
    a.download = ''
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)

    setDownloadConfirmOpen(false)
  }

  // 체험하기 클릭
  const handleExperienceClick = (row) => {
    if(user){
      setExperienceSelected(row);

      if (data?.xr === 'VL-HMD') {
        setDownloadConfirmOpen(true)
        return
      } else {
        setExperienceOpen(true);
      }
    } else {
      setConfirmOpen(true);
    }
  };

  // 체험하기 닫기
  const handleCloseExperience = () => {
    setExperienceOpen(false)
    setIframeKey(prev => prev + 1)
  }

  const handleConfirmAccept = () => {
    setConfirmOpen(false)
    navigate(buildLangHref('/login', lang))
  }

  // 상세조회 API 호출 함수
  const loadDetailData = useCallback(async () => {
    if (!id) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)

      // console.log("XR 상세조회 API 호출:", id);

      const res = await getXrCntsDetail({ cmsContentsId: id, accountId: user?.accountId });

      // console.log(" XR 상세조회 API 응답:", res.data);

      const item = res?.data
      if (!item) {
        setData(null)
        return
      }

      const mapped = {
        id: item.cmsContentsId,
        xr: item.contentType || '',
        title: item.cmsContentsName,
        categoryPath: [
          item.eduGrandDivCode ?? '', // 대분류 카테고리 (코드명으로 변경 필요)
          item.eduMidiumDivCode ?? '', // 중분류 카테고리 (코드명으로 변경 필요)
          item.eduSmallDivCode ?? ''  // 세부 카테고리 (코드명으로 변경 필요)
        ],
        thumbUrl: item?.thumbnailImgUrl,//|| imgArr[Math.floor(Math.random() * imgArr.length)].thumbUrl ,  // 기본 이미지 URL
        interest: item?.concernYn === 'Y', // 관심 여부 (확인 후 값 매핑 필요)
        previewUrl: 'https://knou.gcdn.gov-ntruss.com/knou-test/portal/contents/OVL-2025-010107-0001/preview/preview.mp4',  // 미리보기 URL (확인 후 값 매핑 필요)
        experienceUrl: item?.contentUrl ?? 'https://knou.gcdn.gov-ntruss.com/knou-test/portal/contents/OVL-2025-010107-0001/view/view.mp4', // 체험하기 URL (확인 후 값 매핑 필요)
        // previewUrl: item.cmsMediaFilePath,
        // experienceUrl: item.attachFilePath,
        sections:[
          {
            title: '상세정보',
            paragraphs: <Box dangerouslySetInnerHTML={{ __html: cleanHtml(item?.contentsInfo) }} /> || '',
          },
          {
            title: '지원기기',
            list: [item?.contentsDevice],
          },
          // {
          //   title: '이용안내',
          //   paragraphs: '',
          //   list: ['AR App 다운 방법 : 다운로드 https://example.com/app.apk', '지원 기기 : 갤럭시S7 이상(안드로이드 ver26) 아이폰 6s 이상']
          // },
          {
            title: '저작권 안내',
            paragraphs: '저작권 유형 : CC BY-NC',
            img: imgType1,   // 🔹 이 줄 추가
          },
        ],
      };

      setInterest(item?.concernYn === 'Y')

      setData(mapped);

    } catch (error) {
      console.error("XR 상세조회 실패:", error);
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [id, user, fromList]);

  const insertMyHistoryContents = useCallback(async () => {
    if (!user) return;
    try {
      await insertXrCntsHistory({ accountId: user.accountId, cmsContentsId: id });
    } catch (error) {
      console.error('컨텐츠 본 내역 저장 실패:', error)
    }
  }, [user, id])

  useEffect(() => {
    insertMyHistoryContents();
    loadDetailData();
  }, [loadDetailData, insertMyHistoryContents]);

  // 이미지 alt값
  const licenseAltMap = {
    [imgType1]: '제1유형',
    [imgType2]: '제2유형',
    [imgType3]: '제3유형',
    [imgType4]: '제4유형',
    [imgOpen]: '오픈라이선스'
  };

  const getAltText = (img) => licenseAltMap[img] || '';

  const renderSection = ({ title, img, list, paragraphs, mb = 3 }) => {

    const processDownloadText = (text) => {
      if (!text?.includes('다운로드')) return text;

      // URL 자동 추출
      const urlMatch = text.match(/https?:\/\/[^\s]+/);
      const url = urlMatch ? urlMatch[0] : '#';

      // "다운로드"만 링크 처리
      const [before, after] = text.split('다운로드');

      return (
          <>
            {before}
            <Box
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'primary.main',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
            >
              다운로드
              <OpenInNewOutlinedIcon fontSize="small" sx={{ color: 'primary.main' }} />
            </Box>
            {after.replace(url, '').trim()}
          </>
      );
    };


    return (
        <Stack spacing={1} alignItems="flex-start" sx={{ mb }}>
          <Typography fontWeight={600}>{title}</Typography>

          {/* 저작권 이미지 */}
          {img && (
              <Box
                  component="img"
                  src={img}
                  alt={getAltText(img)}
                  sx={{
                    display: 'block',
                    mb: 1,
                    height: '48px',
                  }}
              />
          )}

          {/* paragraphs */}
          {Array.isArray(paragraphs)
              ? paragraphs.map((p, idx) => (
                  <Typography key={idx} sx={{ whiteSpace: 'pre-line' }}>
                    {p}
                  </Typography>
              ))
              : paragraphs && (
              <Typography sx={{ whiteSpace: 'pre-line' }}>{paragraphs}</Typography>
          )}

          {/* list */}
          {list?.length > 0 && (
              <Stack
                  component="ul"
                  sx={{
                    pl: 2,
                    '& li::marker': {
                      width: '4px',
                      height: '4px',
                      borderRadius: '50%',
                      bgcolor: 'grey.600',
                    },
                  }}
              >
                {list.map((item, idx) => (
                    <Typography component="li" key={idx}>
                      {processDownloadText(item)}
                    </Typography>
                ))}
              </Stack>
          )}
        </Stack>
    );
  };

  const [interest, setInterest] = useState(false);

  const [interestModalOpen, setInterestModalOpen] = useState(false);
  const [interestMessage, setInterestMessage] = useState('');

  const updateInterest = async (chk) => {
    try {
      const params = {
        cmsContentsId: id,
        accountId: user?.accountId
      };
      if(chk) {
        await insertMyContentsConcern(params);
      } else {
        await deleteMyContentsConcern(params);
      }
      setInterest(chk);
      setInterestMessage(chk ? '관심 콘텐츠로 등록되었습니다.': '관심 콘텐츠에서 삭제되었습니다.');
    } catch (error) {
      console.error('Failed to delete interested content', error);
      // Here we could show an error alert to the user.
    }
  }


  const toggleInterest = () => {
    if(!user){
      setConfirmOpen(true);
      return;
    }

    setInterest((prev) => {
      const next = !prev;

      updateInterest(next);
      // if (next) {
      //   setInterestMessage('관심 콘텐츠로 등록되었습니다.');
      // } else {
      //   setInterestMessage('관심 콘텐츠에서 삭제되었습니다.');
      // }

      setInterestModalOpen(true);
      return next;
    });
  };

  // 로딩 상태
  if (loading) {
    return (
        <Box
            sx={{
              width: '100%',
              py: 10,
              textAlign: 'center',
              color: 'text.secondary',
              typography: 'body2',
            }}
        >
          <Typography>XR콘텐츠 정보를 불러오는 중입니다...</Typography>
        </Box>
    )
  }

  if (!data) {
    return (
        <Box
            sx={{
              width: '100%',
              py: 10,
              textAlign: 'center',
              color: 'text.secondary',
              typography: 'body2',
            }}
        >
          <Stack alignItems="center" spacing={1.5}>
            <ErrorOutlineIcon sx={{ fontSize: '32px', color: 'grey.light' }} />
            <Typography color="grey.500">XR콘텐츠 정보를 찾을 수 없습니다.</Typography>
          </Stack>
        </Box>
    )
  }

  return (
      <Box>
        {/* 상단 */}
        <Box sx={{ mb: 6 }}>
          <Stack direction={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'flex-start' : 'stretch' } justifyContent="space-between" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            <Box
                sx={{
                  position: 'relative',
                  width: isMobile ? '100%' : '484px',
                  height: '100%',
                  aspectRatio: '3 / 2',
                  flex: 1,
                }}
            >
              {data.thumbUrl && data.thumbUrl.trim() !== '' ? (
                  <img
                      src={data.thumbUrl}
                      alt={data.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
              ) : (
                  <img
                      src='/assets/images/No image.png'
                      alt='이미지 없음'
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
              )}
            </Box>

            {/* 정보 */}
            <Stack justifyContent="space-between" sx={{ flex: 1, px: isMobile ? 2 : 5, py: 4, width: isMobile ? '100%' : 'auto', gap: isMobile ? 0 : 5 }}>
              <FavoriteIcon
                  onClick={toggleInterest}
                  sx={{
                    color: interest ? 'primary.main' : 'divider',
                    ml: 'auto',
                    cursor: 'pointer',
                  }}
              />
              <Stack>
                {data.xr && (
                    <Chip
                        variant="outlined"
                        color={getXRColor(data.xr)}
                        label={data.xr}
                        sx={{ alignSelf: 'flex-start', mb: 1 }}
                    />
                )}
                <Typography component="span" variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight={500} sx={{ mb: isMobile ? 3 : 0 }}>{data.title}</Typography>
              </Stack>


              {/* 버튼 영역 */}
              <Stack direction="row" sx={{ gap: 1, '& > *': { flex: 1 } }}>
                <Button
                    variant="outlined"
                    color="primary"
                    size="large"
                    onClick={() => handlePreviewClick(data)}
                >
                  미리보기
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={() => handleExperienceClick(data)}
                    //  onClick={() => setConfirmOpen(true)} // 로그인 상태가 아닐때
                >
                  체험하기
                </Button>

                <AlertConfirmModal
                    open={confirmOpen}
                    title="로그인이 필요한 서비스 입니다."
                    description="로그인 페이지로 이동하겠습니까?"
                    type="confirm"
                    onConfirm={handleConfirmAccept}
                    onClose={() => setConfirmOpen(false)}
                    onCancel={() => setConfirmOpen(false)}
                />

                <AlertConfirmModal
                    open={interestModalOpen}
                    title={interestMessage}
                    type="alert"
                    onConfirm={() => setInterestModalOpen(false)}
                    onClose={() => setInterestModalOpen(false)}
                />

                <AlertConfirmModal
                    open={downloadConfirmOpen}
                    title={`${data?.title} 다운로드`}
                    description="해당 콘텐츠 파일을 다운로드 하시겠습니까?"
                    type="confirm"
                    onConfirm={handleDownloadConfirm}
                    onClose={() => setDownloadConfirmOpen(false)}
                    onCancel={() => setDownloadConfirmOpen(false)}
                />

                <BasicModal
                    open={previewOpen}
                    onClose={handleClosePreview}
                    title={previewSelected?.title}
                    disableEnforceFocus
                    disablePortal
                >
                  <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        paddingTop: '56.25%',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}
                  >
                    <Box
                        component="iframe"
                        src={previewSelected?.previewUrl || null}
                        //src="https://knou.gcdn.gov-ntruss.com/knou-test/portal/contents/0NYCZYF6T9T3H/preview/preview.mp4"
                        title="Preview Video"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          border: 0,
                        }}
                    />
                  </Box>
                </BasicModal>

                <BasicModal
                    open={experienceOpen}
                    onClose={handleCloseExperience}
                    title={experienceSelected?.title}
                    disableEnforceFocus
                    disablePortal
                >
                  <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        paddingTop: '56.25%',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}
                  >
                    <Box
                        component="iframe"
                        src={experienceSelected?.experienceUrl || null}
                        // src="https://knou.gcdn.gov-ntruss.com/knou-test/portal/contents/0NYCZYF6T9T3H/view/view.mp4"
                        title="Experience Video"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          border: 0,
                        }}
                    />
                  </Box>
                </BasicModal>

              </Stack>
            </Stack>
          </Stack>
        </Box>

        {/* 소개 */}
        <Box
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              pb: 8,
              mb: 5,
            }}
        >
          {data.sections?.map((section, idx) => (
              <Box key={idx}>
                {renderSection(section)}
              </Box>
          ))}
        </Box>

        <Stack direction="row" justifyContent="flex-end">
          <Button
              variant="contained"
              size="large"
              sx={{ width: isMobile ? '100%' : 'auto'}}
              onClick={() => navigate(buildLangHref('/contents/xr', lang))}
          >
            목록
          </Button>
        </Stack>
      </Box>
  )
}
